import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { euro, dateFR, priceTtcOf } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Copy, Star, Users, Package, ListChecks, FileBarChart, Upload, Globe, Building2, Percent, Image as ImageIcon, CheckCircle2, XCircle, Search, Crop, RotateCcw, CalendarDays, GripVertical } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { ResellersTab } from "@/components/ResellersTab";
import { StockOrdersTab } from "@/components/StockOrdersTab";
import { TranslationHistoryTab } from "@/components/TranslationHistoryTab";
import { CropDialog } from "@/components/CropDialog";
import { EventsTab } from "@/components/EventsTab";
import { PricingParamsTab } from "@/components/PricingParamsTab";
import { compressImage } from "@/lib/compressImage";
import { OptionsTrash } from "@/components/OptionsTrash";
import { ColorPhotos } from "@/components/ColorPhotos";


function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/15 grid place-items-center text-primary">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-bold font-mono">{value}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_ITEM = { name: "", category: "", reseller_ht: 0, public_ht: 0, public_ttc: 0, image: "", active: true, ranges: null, van_transport: ["droit", "diagonal"], excluded_models: [], max_qty: 1, max_qty_by_model: {}, preselected_models: [] };

function AvailabilitySection({ form, setForm, ranges, allModels }) {
  const curRanges = form.ranges && form.ranges.length ? form.ranges : [form.range_key];
  const vt = form.van_transport && form.van_transport.length ? form.van_transport : ["droit", "diagonal"];
  const excluded = form.excluded_models || [];

  const toggleRange = (key) => {
    const next = curRanges.includes(key) ? curRanges.filter((r) => r !== key) : [...curRanges, key];
    if (!next.length) return;
    setForm({ ...form, ranges: next, range_key: next.includes(form.range_key) ? form.range_key : next[0] });
  };
  const toggleVt = (t) => {
    const next = vt.includes(t) ? vt.filter((x) => x !== t) : [...vt, t];
    setForm({ ...form, van_transport: next });
  };
  const toggleModel = (id) => {
    const next = excluded.includes(id) ? excluded.filter((x) => x !== id) : [...excluded, id];
    setForm({ ...form, excluded_models: next });
  };
  const transportOf = (m) => {
    const c = (m.category || "").toUpperCase(); const n = (m.name || "").toLowerCase();
    return (c.includes("DIAGONAL") || n.includes("oblic") || n.includes("icelandic")) ? "diagonal" : "droit";
  };
  const visibleModels = curRanges.flatMap((rk) => (allModels[rk] || [])
    .filter((m) => rk !== "van" || vt.includes(transportOf(m))));
  const uncheckAll = () => {
    const ids = visibleModels.map((m) => m.id);
    setForm({ ...form, excluded_models: Array.from(new Set([...excluded, ...ids])) });
  };
  const checkAll = () => {
    const ids = new Set(visibleModels.map((m) => m.id));
    setForm({ ...form, excluded_models: excluded.filter((id) => !ids.has(id)) });
  };
  const maxQty = form.max_qty ?? 1;
  const mbm = form.max_qty_by_model || {};
  const setMaxQty = (v) => setForm({ ...form, max_qty: Math.max(0, parseInt(v || "0", 10) || 0) });
  const setModelMax = (id, v) => {
    const next = { ...mbm };
    if (v === "" || v == null) delete next[id];
    else next[id] = Math.max(0, parseInt(v, 10) || 0);
    setForm({ ...form, max_qty_by_model: next });
  };
  const preselected = form.preselected_models || [];
  const togglePre = (id) => {
    const next = preselected.includes(id) ? preselected.filter((x) => x !== id) : [...preselected, id];
    setForm({ ...form, preselected_models: next });
  };

  return (
    <div className="border-t border-border pt-4 space-y-4">
      <div>
        <Label className="text-sm font-semibold">Disponible dans les gammes</Label>
        <div className="flex gap-4 mt-2">
          {ranges.map((r) => (
            <label key={r.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`avail-range-${r.key}`}>
              <Checkbox checked={curRanges.includes(r.key)} onCheckedChange={() => toggleRange(r.key)} />
              {r.name}
            </label>
          ))}
        </div>
      </div>

      {curRanges.includes("van") && (
        <div>
          <Label className="text-sm font-semibold">Van chevaux — type de transport</Label>
          <div className="flex gap-4 mt-2">
            {[["droit", "Transport droit"], ["diagonal", "Transport diagonale"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`avail-vt-${k}`}>
                <Checkbox checked={vt.includes(k)} onCheckedChange={() => toggleVt(k)} />
                {lbl}
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <Label className="text-sm font-semibold">Quantité maximum sélectionnable</Label>
        <div className="flex items-center gap-2 mt-2">
          <Input type="number" min={0} value={maxQty} onChange={(e) => setMaxQty(e.target.value)}
            data-testid="avail-max-qty" className="w-24 font-mono" />
          <span className="text-xs text-muted-foreground">Quantité par défaut pour tous les modèles. Vous pouvez la surcharger modèle par modèle ci-dessous.</span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Modèles concernés <span className="font-normal text-muted-foreground">(décochez pour exclure · ★ = pré-cochée par défaut · nombre = quantité max)</span></Label>
          <div className="flex gap-3 text-xs">
            <button type="button" onClick={checkAll} data-testid="avail-check-all" className="text-primary hover:underline">Tout cocher</button>
            <button type="button" onClick={uncheckAll} data-testid="avail-uncheck-all" className="text-primary hover:underline">Tout décocher</button>
          </div>
        </div>
        <div className="mt-2 max-h-72 overflow-auto rounded-lg border border-border p-3 grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
          {visibleModels.map((m) => {
            const checked = !excluded.includes(m.id);
            const pre = preselected.includes(m.id);
            return (
              <div key={m.id} className="flex items-center gap-2 py-0.5" data-testid={`avail-model-${m.id}`}>
                <Checkbox checked={checked} onCheckedChange={() => toggleModel(m.id)} />
                <span className="flex-1 text-sm cursor-pointer leading-snug break-words" onClick={() => toggleModel(m.id)}>{m.name}</span>
                <button type="button" disabled={!checked} onClick={() => togglePre(m.id)}
                  data-testid={`avail-model-pre-${m.id}`} title="Pré-cochée par défaut"
                  className={`shrink-0 disabled:opacity-30 ${pre ? "text-amber-500" : "text-muted-foreground/40 hover:text-muted-foreground"}`}>
                  <Star className={`w-4 h-4 ${pre ? "fill-amber-500" : ""}`} />
                </button>
                <Input type="number" min={0} disabled={!checked} value={mbm[m.id] ?? ""} placeholder={String(maxQty)}
                  onChange={(e) => setModelMax(m.id, e.target.value)} data-testid={`avail-model-max-${m.id}`}
                  title="Quantité max pour ce modèle" className="w-14 h-7 text-xs font-mono px-1.5" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ImageField({ value, onChange, testid, original, onCrop }) {
  const [busy, setBusy] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const inputId = `${testid}-file`;
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(f));
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Photo téléversée");
    } catch (err) { toast.error("Échec du téléversement"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <div className="w-24 h-24 rounded-lg border border-border bg-white grid place-items-center overflow-hidden shrink-0">
        {value ? <img src={value} alt="" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-muted-foreground">Aucune</span>}
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="gap-1" disabled={busy}
            onClick={() => document.getElementById(inputId).click()} data-testid={`${testid}-replace`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Remplacer
          </Button>
          {value && onCrop && <Button type="button" variant="outline" size="sm" className="gap-1"
            onClick={() => setCropOpen(true)} data-testid={`${testid}-crop`}><Crop className="w-4 h-4" /> Recadrer</Button>}
          {onCrop && original && original !== value && <Button type="button" variant="ghost" size="sm" className="gap-1"
            onClick={() => onCrop(original, "")} data-testid={`${testid}-restore`}><RotateCcw className="w-4 h-4" /> Photo d'origine</Button>}
          {value && <Button type="button" variant="ghost" size="sm" className="text-destructive"
            onClick={() => onChange("")} data-testid={`${testid}-clear`}>Retirer</Button>}
          <input id={inputId} type="file" accept="image/*" className="hidden" onChange={onFile} />
        </div>
        {onCrop && <CropDialog open={cropOpen} onOpenChange={setCropOpen} src={original || value} onCropped={(url) => onCrop(url, original || value)} />}
        <div className="flex items-center gap-2">
          <Input value={value || ""} placeholder="…ou coller une URL d'image" onChange={(e) => onChange(e.target.value)}
            className="h-8 text-xs" data-testid={`${testid}-url`} />
        </div>
      </div>
    </div>
  );
}

function IncludedOptionsSection({ form, setForm, options }) {
  const [q, setQ] = useState("");
  const sel = form.included_options || [];
  const toggle = (id) => setForm({ ...form, included_options: sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id] });
  const shown = options.filter((o) => sel.includes(o.id) || !q || o.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid="included-options">
      <Label>Options incluses dans ce modèle <span className="text-xs text-muted-foreground font-normal">(ex. version Premium : une remise promo sur ces options baisse automatiquement le prix du modèle)</span></Label>
      <Input placeholder="Rechercher une option…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-xs" data-testid="included-options-search" />
      <div className="max-h-40 overflow-y-auto grid sm:grid-cols-2 gap-1">
        {shown.map((o) => (
          <label key={o.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`included-opt-${o.id}`}>
            <Checkbox checked={sel.includes(o.id)} onCheckedChange={() => toggle(o.id)} />{o.name}
          </label>
        ))}
      </div>
    </div>
  );
}

function ItemDialog({ trigger, title, initial, ranges, onSubmit, showRange, isOption, allModels, allOptions, categories = {} }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  useEffect(() => { if (open) setForm(initial); }, [open, initial]);

  const submit = async () => {
    await onSubmit(form);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="w-[96vw] max-w-[1200px] sm:max-w-[1200px] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {showRange && (
            <div>
              <Label>Gamme</Label>
              <Select value={form.range_key} onValueChange={(v) => setForm({ ...form, range_key: v })}>
                <SelectTrigger className="mt-1.5" data-testid="item-range"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {ranges.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Nom</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="item-name" className="mt-1.5" />
          </div>
          <div>
            <Label>Catégorie</Label>
            <Select value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1.5" data-testid="item-category"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
              <SelectContent>
                {[...new Set([...(categories[form.range_key] || []), ...(form.category ? [form.category] : [])])].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!isOption && (
            <div>
              <Label>Détails / équipements (affichés via l'icône info)</Label>
              <Textarea value={form.details || ""} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={2} data-testid="item-details" className="mt-1.5" placeholder="Ex. 2 baies flexibles + protections latérales + matelassures" />
            </div>
          )}
          {!isOption && (
            <div>
              <Label>Couleurs (une par ligne : Nom | #code | standard ou spéciale)</Label>
              <Textarea rows={4} data-testid="item-colors" className="mt-1.5 font-mono text-xs"
                defaultValue={(form.colors || []).map((c) => `${c.name} | ${c.hex} | ${c.type === "special" ? "spéciale" : "standard"}`).join("\n")}
                onChange={(e) => setForm({ ...form, colors: e.target.value.split("\n").map((l) => l.split("|").map((x) => x.trim())).filter((p) => p[0]).map(([name, hex, t]) => ({ name, hex: hex || "#FFFFFF", type: /sp/i.test(t || "") ? "special" : "standard" })) })}
                placeholder={"Blanc | #FFFFFF | standard\nBleu pastel | #6391b0 | spéciale"} />
              <p className="text-[11px] text-muted-foreground mt-1">Les couleurs spéciales ajoutent automatiquement l'option « Option couleur spéciale » (ou « Autre RAL ») disponible pour le modèle.</p>
            </div>
          )}
          {!isOption && form.id && form.image && <ColorPhotos modelId={form.id} colors={initial.colors} />}
          {isOption && (
            <div>
              <Label>Description (affichée via l'icône info)</Label>
              <Textarea value={form.details || ""} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={2} data-testid="item-details" className="mt-1.5" placeholder="Décrivez l'option (visible par le vendeur via l'icône info)" />
            </div>
          )}
          <div>
            <Label>{isOption ? "F2 possibles quand cette option est cochée (kg, séparés par des virgules)" : "F2 (Poids total autorisé en charge) possibles (kg, séparés par des virgules)"}</Label>
            <Input defaultValue={(form.ptac_values || []).join(", ")} data-testid="item-ptac" className="mt-1.5 font-mono text-xs"
              placeholder="Ex. 2600, 2500, 2400, 2300, 2200, 2100, 2000"
              onChange={(e) => setForm({ ...form, ptac_values: [...new Set(e.target.value.split(/[^\d]+/).filter(Boolean).map(Number).filter((n) => n >= 100))].sort((a, b) => b - a) })} />
            <p className="text-[11px] text-muted-foreground mt-1">{isOption ? "Laisser vide si l'option ne change pas le PTAC." : "Liste proposée dans le configurateur."}</p>
            {(form.ptac_values || []).length > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <Label className="text-xs">F2 par défaut</Label>
                <select value={form.ptac_default || ""} onChange={(e) => setForm({ ...form, ptac_default: e.target.value ? Number(e.target.value) : null })}
                  data-testid="item-ptac-default" className="h-8 rounded-md border border-border bg-background px-2 text-sm font-mono">
                  <option value="">Le plus élevé ({Math.max(...form.ptac_values)} kg)</option>
                  {form.ptac_values.map((v) => <option key={v} value={v}>{v} kg</option>)}
                </select>
              </div>
            )}
          </div>
          {!isOption && (
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label>Équipements de série (un par ligne, affichés sur les affiches de prix)</Label>
                <Textarea value={form.equip_text ?? (form.standard_equipment || []).join("\n")} rows={5} data-testid="item-standard-equipment" className="mt-1.5 text-sm"
                  onChange={(e) => setForm({ ...form, equip_text: e.target.value, standard_equipment: e.target.value.split(/\n+/).map((s) => s.trim()).filter(Boolean) })} />
              </div>
              <div>
                <Label>Poids à vide (kg)</Label>
                <Input value={form.empty_weight || ""} data-testid="item-empty-weight" className="mt-1.5 font-mono" placeholder="Ex. 880 ou 770 à 860"
                  onChange={(e) => setForm({ ...form, empty_weight: e.target.value })} />
                <p className="text-[11px] text-muted-foreground mt-1">Affichable sur l'affiche de prix (case à cocher). Une valeur ou une fourchette.</p>
              </div>
            </div>
          )}
          <div>
            <Label>Photo</Label>
            <div className="mt-1.5">
              <ImageField value={form.image || ""} original={form.image_original || ""}
                onChange={(v) => setForm({ ...form, image: v, image_original: "" })}
                onCrop={(img, orig) => setForm({ ...form, image: img, image_original: orig })} testid="item-image" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Revendeur HT</Label>
              <Input type="number" value={form.reseller_ht}
                onChange={(e) => setForm({ ...form, reseller_ht: Number(e.target.value) })}
                data-testid="item-reseller-ht" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-xs">Public HT</Label>
              <Input type="number" value={form.public_ht}
                onChange={(e) => { const v = Number(e.target.value); setForm({ ...form, public_ht: v, public_ttc: Math.round(v * 1.2 * 100) / 100 }); }}
                data-testid="item-public-ht" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-xs">Public TTC</Label>
              <Input type="number" value={form.public_ttc}
                onChange={(e) => { const v = Number(e.target.value); setForm({ ...form, public_ttc: v, public_ht: Math.round(v / 1.2 * 100) / 100 }); }}
                data-testid="item-public-ttc" className="mt-1.5 font-mono" />
            </div>
          </div>
          {isOption && <AvailabilitySection form={form} setForm={setForm} ranges={ranges} allModels={allModels || {}} />}
          {!isOption && <IncludedOptionsSection form={form} setForm={setForm} options={allOptions || []} />}
        </div>
        <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 mt-2 bg-background px-6 py-4 border-t border-border">
          <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
          <Button onClick={submit} data-testid="item-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogTab({ kind, ranges }) {
  const [rangeKey, setRangeKey] = useState(ranges[0]?.key || "van");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allModels, setAllModels] = useState({});
  const [allOptions, setAllOptions] = useState([]);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const endpoint = kind === "model" ? "models" : "options";
  const listKey = kind === "model" ? "models" : "options";

  useEffect(() => {
    if (kind !== "model") return;
    api.get(`/catalog/${rangeKey}`).then(({ data }) => setAllOptions(data.options || []));
  }, [kind, rangeKey]);

  const [allCats, setAllCats] = useState({});
  useEffect(() => {
    Promise.all(ranges.map((r) => api.get(`/catalog/${r.key}`).then(({ data }) => [r.key, data])))
      .then((pairs) => {
        if (kind === "option") setAllModels(Object.fromEntries(pairs.map(([k, d]) => [k, d.models])));
        setAllCats(Object.fromEntries(pairs.map(([k, d]) => [k, [...new Set((d[listKey] || []).map((x) => x.category).filter(Boolean))].sort()])));
      });
  }, [kind, ranges, listKey]);

  const load = () => {
    setLoading(true);
    api.get(`/catalog/${rangeKey}`).then(({ data }) => setItems(data[listKey])).finally(() => setLoading(false));
  };
  useEffect(load, [rangeKey]);
  useEffect(() => { setActiveCat("all"); setQuery(""); }, [rangeKey]);

  const catOf = (it) => ((it.category || "").trim()) || "Divers";
  const cats = Array.from(new Set(items.map(catOf))).sort((a, b) => a.localeCompare(b, "fr"));
  const q = query.trim().toLowerCase();
  const filteredItems = items.filter((it) => {
    const catOk = activeCat === "all" || catOf(it) === activeCat;
    const qOk = !q || (it.name || "").toLowerCase().includes(q) || catOf(it).toLowerCase().includes(q);
    return catOk && qOk;
  });

  const create = async (form) => {
    try {
      const { data } = await api.post(`/admin/${endpoint}`, { ...form, range_key: form.range_key || rangeKey });
      if (data.range_key === rangeKey || (data.ranges || []).includes(rangeKey)) setItems((xs) => [...xs, data]);
      toast.success("Ajouté");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const update = async (id, form) => {
    try {
      const { data } = await api.put(`/admin/${endpoint}/${id}`, form);
      const here = data.range_key === rangeKey || (kind === "option" && (data.ranges || []).includes(rangeKey));
      setItems((xs) => (here ? xs.map((x) => (x.id === id ? data : x)) : xs.filter((x) => x.id !== id)));
      toast.success(here ? "Modifié" : "Modifié et déplacé dans l'autre gamme");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Supprimé");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const [dragId, setDragId] = useState(null);
  const canDrag = !q;
  const dropOn = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const seq = filteredItems.map((x) => x.id).filter((x) => x !== dragId);
    seq.splice(seq.indexOf(targetId) + (filteredItems.findIndex((x) => x.id === dragId) < filteredItems.findIndex((x) => x.id === targetId) ? 1 : 0), 0, dragId);
    const byId = Object.fromEntries(items.map((x) => [x.id, x]));
    const visible = new Set(seq);
    let k = 0;
    const next = items.map((x) => (visible.has(x.id) ? byId[seq[k++]] : x));
    const prev = items;
    setItems(next);
    setDragId(null);
    try { await api.post(`/admin/${endpoint}/reorder`, { ids: next.map((x) => x.id) }); toast.success("Ordre enregistré"); }
    catch (err) { setItems(prev); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const duplicate = async (it) => {
    const { id, order, _id, ...rest } = it;
    try {
      const { data } = await api.post(`/admin/${endpoint}`, { ...rest, range_key: it.range_key || rangeKey, name: `${it.name} (copie)` });
      setItems((xs) => [...xs, data]);
      toast.success("Option dupliquée");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Select value={rangeKey} onValueChange={setRangeKey}>
          <SelectTrigger className="w-56" data-testid="catalog-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ranges.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <ItemDialog
          title={kind === "model" ? "Nouveau modèle" : "Nouvelle option"}
          initial={{ ...EMPTY_ITEM, range_key: rangeKey, ranges: [rangeKey] }}
          ranges={ranges}
          onSubmit={create}
          isOption={kind === "option"}
          allModels={allModels}
          allOptions={allOptions}
          categories={allCats}
          trigger={<Button className="gap-1" data-testid="add-item"><Plus className="w-4 h-4" /> Ajouter</Button>}
        />
        <OptionsTrash kind={kind === "option" ? "options" : "models"} onRestored={(d) => { if (d.range_key === rangeKey || (d.ranges || []).includes(rangeKey)) setItems((xs) => [...xs, d]); }} />
      </div>

      <div className="space-y-2.5" data-testid="catalog-filters">
        <div className="relative max-w-sm">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={kind === "model" ? "Rechercher un modèle…" : "Rechercher une option…"} data-testid="catalog-search" className="pl-9 h-9" />
        </div>
        {cats.length > 1 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mr-1">Catégories :</span>
            <button type="button" onClick={() => setActiveCat("all")} data-testid="catalog-cat-all"
              className={`text-xs font-semibold rounded-full px-3 py-1 border transition-colors ${activeCat === "all" ? "bg-[#9E7B41] text-white border-[#9E7B41]" : "bg-transparent text-muted-foreground border-border"}`}>
              Toutes
            </button>
            {cats.map((c) => (
              <button key={c} type="button" onClick={() => setActiveCat(c)} data-testid={`catalog-cat-${c}`}
                className={`text-xs font-semibold rounded-full px-3 py-1 border transition-colors ${activeCat === c ? "bg-[#9E7B41] text-white border-[#9E7B41]" : "bg-transparent text-muted-foreground border-border"}`}>
                {c}
              </button>
            ))}
          </div>
        )}
        <div className="text-xs text-muted-foreground" data-testid="catalog-count">{filteredItems.length} / {items.length} {kind === "model" ? "modèle(s)" : "option(s)"}</div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {<TableHead className="w-8" title={canDrag ? "Glisser pour réordonner" : "Videz la recherche pour réordonner"} />}
                <TableHead>Nom</TableHead>
                <TableHead>Catégorie</TableHead>
                {kind === "model" && <TableHead className="text-center">Photo</TableHead>}
                {kind === "option" && <TableHead className="text-center">Photo</TableHead>}
                <TableHead className="text-right">Revendeur HT</TableHead>
                <TableHead className="text-right">Public HT</TableHead>
                <TableHead className="text-right">TTC</TableHead>
                {kind === "option" && <TableHead className="text-right">Qté max</TableHead>}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((it) => (
                <TableRow key={it.id} data-testid={`catalog-row-${it.id}`}
                  draggable={canDrag} onDragStart={() => setDragId(it.id)} onDragEnd={() => setDragId(null)}
                  onDragOver={(e) => { if (canDrag) e.preventDefault(); }} onDrop={() => dropOn(it.id)}
                  className={dragId === it.id ? "opacity-40" : ""}>
                  {(
                    <TableCell className="w-8 px-2">
                      <GripVertical className={`w-4 h-4 ${canDrag ? "cursor-grab text-muted-foreground" : "text-muted-foreground/30"}`} data-testid={`drag-handle-${it.id}`} />
                    </TableCell>
                  )}
                  <TableCell className="max-w-[280px]"><span className="text-sm">{it.name}</span></TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">{it.category || "—"}</Badge></TableCell>
                  {(kind === "model" || kind === "option") && (
                    <TableCell className="text-center" data-testid={`row-photo-${it.id}`}>
                      {(it.image || "").trim() ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600" data-testid={`photo-yes-${it.id}`}>
                          <CheckCircle2 className="w-4 h-4" /> Oui
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground" data-testid={`photo-no-${it.id}`}>
                          <XCircle className="w-4 h-4" /> Non
                        </span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right font-mono text-sm">{euro(it.reseller_ht)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{euro(it.public_ht)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-[#c9a227]">{euro(it.public_ttc)}</TableCell>
                  {kind === "option" && (
                    <TableCell className="text-right font-mono text-sm" data-testid={`row-maxqty-${it.id}`}>
                      {it.max_qty ?? 1}
                      {it.max_qty_by_model && Object.keys(it.max_qty_by_model).length > 0 && (
                        <span className="text-[10px] text-muted-foreground ml-1">(+ surcharges)</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <ItemDialog
                        title="Modifier"
                        initial={it}
                        ranges={ranges}
                        onSubmit={(form) => update(it.id, form)}
                        isOption={kind === "option"}
                        allModels={allModels}
                        allOptions={allOptions}
                        categories={allCats}
                        trigger={<Button size="icon" variant="ghost" data-testid={`edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>}
                      />
                      <Button size="icon" variant="ghost" title="Dupliquer"
                        onClick={() => duplicate(it)} data-testid={`dup-${it.id}`}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <ConfirmDelete label={kind === "model" ? "ce modèle" : "cette option"} name={it.name} onConfirm={() => remove(it.id)} testid={`del-${it.id}`}>
                        <Button size="icon" variant="ghost" className="text-destructive" data-testid={`del-${it.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </ConfirmDelete>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function ImportTab({ ranges }) {
  const [rangeKey, setRangeKey] = useState(ranges[0]?.key || "van");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doImport = async () => {
    if (!file) return toast.error("Choisissez un fichier .xlsx");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", "replace");
      const { data } = await api.post(`/admin/import/${rangeKey}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Import réussi : ${data.models} modèles, ${data.options} options`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 grid place-items-center text-primary shrink-0">
            <Upload className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display font-bold">Importer le tarif Excel</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Importez directement vos fichiers tarifs. Le catalogue de la gamme choisie sera remplacé.
              Van&nbsp;: feuilles <span className="font-mono">FRANCE Vans</span> + <span className="font-mono">FRA Options</span>.
              Pro&amp;Sport&nbsp;: feuilles <span className="font-mono">MODELES</span> + <span className="font-mono">FRA Options</span>.
            </p>
          </div>
        </div>

        <div>
          <Label>Gamme à mettre à jour</Label>
          <Select value={rangeKey} onValueChange={setRangeKey}>
            <SelectTrigger className="mt-1.5" data-testid="import-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ranges.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Fichier tarif (.xlsx)</Label>
          <Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="import-file" className="mt-1.5" />
        </div>

        <Button onClick={doImport} disabled={busy} data-testid="import-submit" className="gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Importer et remplacer
        </Button>

        {result && (
          <div className="rounded-lg bg-[#9E7B41]/10 border border-[#9E7B41]/30 p-4 text-sm" data-testid="import-result">
            Import réussi — <strong>{result.models}</strong> modèles et <strong>{result.options}</strong> options pour « {ranges.find((r) => r.key === result.range_key)?.name} ».
          </div>
        )}
      </div>
    </div>
  );
}

function CountriesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", name: "", vat_rate: 20 });
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/countries").then(({ data }) => setList(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const payload = { code: form.code.toUpperCase(), name: form.name, vat_rate: Number(form.vat_rate) / 100 };
      const { data } = await api.post("/admin/countries", payload);
      setList((xs) => {
        const others = xs.filter((c) => c.code !== data.code);
        return [...others, data].sort((a, b) => a.name.localeCompare(b.name));
      });
      toast.success("Pays enregistré");
      setOpen(false);
      setForm({ code: "", name: "", vat_rate: 20 });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (code) => {
    try {
      await api.delete(`/admin/countries/${code}`);
      setList((xs) => xs.filter((c) => c.code !== code));
      toast.success("Supprimé");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-1" data-testid="add-country"><Plus className="w-4 h-4" /> Nouveau pays</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Pays / taux de TVA</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Code (ex: FR, DE)</Label><Input value={form.code} maxLength={3}
                onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="country-code" className="mt-1.5 uppercase" /></div>
              <div><Label>Nom</Label><Input value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="country-name" className="mt-1.5" /></div>
              <div><Label>TVA (%)</Label><Input type="number" step="0.1" value={form.vat_rate}
                onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="country-vat" className="mt-1.5 font-mono" /></div>
            </div>
            <DialogFooter>
              <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
              <Button onClick={save} data-testid="country-save">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto max-w-2xl">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Pays</TableHead>
              <TableHead className="text-right">TVA</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.code} data-testid={`country-row-${c.code}`}>
                  <TableCell className="font-mono">{c.code}</TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell className="text-right font-mono">{Math.round(c.vat_rate * 1000) / 10}%</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="text-destructive"
                      onClick={() => remove(c.code)} data-testid={`del-country-${c.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

const REG_FIELDS = [["reg_cg", "Carte grise", 13.76], ["reg_plate", "Frais de plaque", 30], ["reg_admin", "Frais administratifs (prix revendeur)", 46.24], ["reg_admin_public", "Frais administratifs (prix public)", 86.24]];

function CompanyTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/company").then(({ data }) => setForm(data)).catch(() => setForm({})); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/company", {
        company: form.company || "", address: form.address || "", siret: form.siret || "",
        tva: form.tva || "", phone: form.phone || "", email: form.email || "", conditions: form.conditions || "",
        deposit_pct: Math.min(100, Math.max(0, Number(form.deposit_pct ?? 35) || 0)),
        ...Object.fromEntries(REG_FIELDS.map(([k, , d]) => [k, Math.max(0, Number(form[k] ?? d) || 0)])),
      });
      toast.success("Coordonnées enregistrées");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  if (!form) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  return (
    <div className="max-w-2xl space-y-4">
      <p className="text-sm text-muted-foreground">Ces coordonnées et conditions apparaissent en pied de page des devis PDF et dans l'email envoyé au client.</p>
      <div><Label>Raison sociale</Label><Input value={form.company || ""} onChange={(e) => set("company", e.target.value)} data-testid="company-name" className="mt-1.5" /></div>
      <div><Label>Adresse</Label><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="company-address" className="mt-1.5" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>SIRET</Label><Input value={form.siret || ""} onChange={(e) => set("siret", e.target.value)} data-testid="company-siret" className="mt-1.5" /></div>
        <div><Label>N° TVA</Label><Input value={form.tva || ""} onChange={(e) => set("tva", e.target.value)} data-testid="company-tva" className="mt-1.5" /></div>
        <div><Label>Téléphone</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="company-phone" className="mt-1.5" /></div>
        <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="company-email" className="mt-1.5" /></div>
        <div><Label>Acompte par défaut (%)</Label><Input type="number" min={0} max={100} step="0.5" value={form.deposit_pct ?? 35} onChange={(e) => set("deposit_pct", e.target.value)} data-testid="company-deposit-pct" className="mt-1.5" />
          <p className="text-xs text-muted-foreground mt-1">Proposé automatiquement sur chaque nouveau devis (modifiable devis par devis).</p></div>
      </div>
      <div className="rounded-lg border border-border p-3 space-y-2" data-testid="company-reg-fees">
        <div className="text-sm font-semibold">Frais d'immatriculation (France, € TTC)</div>
        <div className="grid grid-cols-3 gap-3">
          {REG_FIELDS.map(([k, label, d]) => (
            <div key={k}><Label className="text-xs">{label}</Label><Input type="number" min={0} step="0.01" value={form[k] ?? d} onChange={(e) => set(k, e.target.value)} data-testid={`company-${k}`} className="mt-1" /></div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Appliqués aux nouveaux devis et aux devis modifiés ensuite. Carte grise sans TVA, plaque et frais administratifs à 20 %.</p>
      </div>
      <div><Label>Conditions de vente</Label><Textarea rows={6} value={form.conditions || ""} onChange={(e) => set("conditions", e.target.value)} data-testid="company-conditions" className="mt-1.5" /></div>
      <Button onClick={save} disabled={saving} data-testid="company-save" className="gap-1">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer
      </Button>
    </div>
  );
}

function SchematicsTab({ ranges }) {
  const [rangeKey, setRangeKey] = useState(ranges[0]?.key || "van");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get(`/menu/${rangeKey}`).then(({ data }) => setGroups(data.groups || [])).finally(() => setLoading(false)); };
  useEffect(load, [rangeKey]);
  const setImage = async (groupKey, image) => {
    try {
      await api.put("/admin/menu-image", { range_key: rangeKey, group_key: groupKey, image });
      setGroups((gs) => gs.map((g) => (g.key === groupKey ? { ...g, image } : g)));
      toast.success("Schéma mis à jour");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Remplacez le schéma affiché sur la page de choix du modèle (par gamme).</p>
      <div className="flex items-center gap-3">
        <Label>Gamme</Label>
        <Select value={rangeKey} onValueChange={setRangeKey}>
          <SelectTrigger className="w-56" data-testid="schema-range"><SelectValue /></SelectTrigger>
          <SelectContent>{ranges.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map((g) => (
            <div key={g.key} className="rounded-xl border border-border bg-card p-4" data-testid={`schema-${g.key}`}>
              <div className="font-semibold text-sm mb-2">{g.label}</div>
              <ImageField value={g.image || ""} onChange={(v) => setImage(g.key, v)} testid={`schema-img-${g.key}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PromotionsTab({ ranges }) {
  const empty = { id: null, name: "", active: true, range_key: ranges[0]?.key || "van", country_codes: [], start_date: "", end_date: "", model_rules: {}, option_rules: {}, no_option_promo_models: [], reseller_ids: [], all_resellers: true, model_amount_pub_ttc: 0, model_amount_res_ht: 0, default_off: false, precheck_off_ids: [], hide_in_prices: false, exclusive: false, is_aid: false };
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [cat, setCat] = useState({ models: [], options: [] });
  const [q, setQ] = useState("");
  const [bulkPub, setBulkPub] = useState("");
  const [bulkRes, setBulkRes] = useState("");
  const [ruleTab, setRuleTab] = useState("option");
  const [promoCat, setPromoCat] = useState("all");
  const [lowList, setLowList] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/promotions").then(({ data }) => setList(data)).finally(() => setLoading(false)); };
  useEffect(load, []);
  useEffect(() => { api.get("/countries").then(({ data }) => setCountries(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/resellers").then(({ data }) => setResellers(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!open || !form.range_key) return;
    api.get(`/catalog/${form.range_key}`).then(({ data }) => setCat(data)).catch(() => setCat({ models: [], options: [] }));
  }, [open, form.range_key]);

  const openNew = () => { setForm(empty); setQ(""); setOpen(true); };
  const openEdit = (p) => {
    const codes = p.country_codes || [];
    const ids = (p.reseller_ids || []).length ? p.reseller_ids : resellersIn(codes, p.range_key);
    setForm({ ...empty, ...p, all_resellers: !!p.all_resellers, country_codes: codes, model_rules: p.model_rules || {}, option_rules: p.option_rules || {}, no_option_promo_models: p.no_option_promo_models || [], reseller_ids: ids });
    setQ(""); setOpen(true);
  };
  const openDuplicate = (p) => {
    openEdit({ ...p, id: undefined, name: `${p.name} (copie)`, active: false });
    toast.info("Copie créée : modifiez-la puis enregistrez (elle est inactive par défaut).");
  };
  const togglePrecheckOff = (id) => setForm((f) => { const cur = f.precheck_off_ids || []; return { ...f, precheck_off_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] }; });
  const toggleReseller = (id) => setForm((f) => ({ ...f, reseller_ids: (f.reseller_ids || []).includes(id) ? f.reseller_ids.filter((x) => x !== id) : [...(f.reseller_ids || []), id] }));
  const toggleNoOpt = (id) => setForm((f) => { const cur = f.no_option_promo_models || []; return { ...f, no_option_promo_models: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] }; });
  const setAllNoOpt = (on) => setForm((f) => { const ids = cat.models.filter(matchItem).map((m) => m.id); const cur = new Set(f.no_option_promo_models || []); ids.forEach((id) => (on ? cur.add(id) : cur.delete(id))); return { ...f, no_option_promo_models: [...cur] }; });
  const bulkBtns = (onAll, onNone, tid) => (
    <span className="inline-flex gap-1 ml-2">
      <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={onAll} data-testid={`${tid}-all`}>Tout cocher</Button>
      <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-destructive" onClick={onNone} data-testid={`${tid}-none`}>Tout décocher</Button>
    </span>
  );
  const rangeResellers = resellers.filter((r) => (r.sectors || []).some((s) => s.range_key === form.range_key));
  const resellersIn = (codes, rk) => resellers.filter((r) => (r.sectors || []).some((s) => s.range_key === rk && codes.includes(s.country))).map((r) => r.id);
  const applyCountries = (codes) => setForm((f) => {
    const added = codes.filter((c) => !f.country_codes.includes(c));
    const removed = f.country_codes.filter((c) => !codes.includes(c));
    const keep = new Set(resellersIn(codes, f.range_key));
    const drop = new Set(resellersIn(removed, f.range_key).filter((id) => !keep.has(id)));
    const ids = new Set((f.reseller_ids || []).filter((id) => !drop.has(id)));
    resellersIn(added, f.range_key).forEach((id) => ids.add(id));
    return { ...f, country_codes: codes, reseller_ids: [...ids] };
  });
  const toggleCountry = (code) => applyCountries(form.country_codes.includes(code) ? form.country_codes.filter((c) => c !== code) : [...form.country_codes, code]);
  const setRule = (kind, id, field, val) => setForm((f) => {
    const key = kind === "model" ? "model_rules" : "option_rules";
    const cur = { ...(f[key] || {}) };
    const entry = { ...(cur[id] || { pub: 0, res: 0 }) };
    const v = Math.max(0, Math.min(100, Number(val) || 0));
    if (kind === "option" && field === "pub" && (!Number(entry.res) || Number(entry.res) === Number(entry.pub))) entry.res = v;
    entry[field] = v;
    cur[id] = entry;
    return { ...f, [key]: cur };
  });
  const save = async (force = false) => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) { toast.error("La date de fin ne peut pas être antérieure à la date de début."); return; }
    if (force !== true) {
      const low = lowMargins();
      if (low.length) { setLowList(low); return; }
    }
    setLowList(null);
    if (!form.country_codes.length) toast.warning("Aucun pays ciblé : cette promotion ne s'appliquera à aucun devis.");
    const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, r]) => (r.pub || 0) > 0 || (r.res || 0) > 0));
    const payload = { ...form, model_rules: clean(form.model_rules), option_rules: clean(form.option_rules) };
    try {
      await api.post("/admin/promotions", payload);
      toast.success("Promotion enregistrée");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const remove = async (id) => { try { await api.delete(`/admin/promotions/${id}`); setList((xs) => xs.filter((p) => p.id !== id)); toast.success("Supprimée"); } catch (e) { toast.error("Erreur"); } };

  const applyAll = (kind) => {
    const items = (kind === "model" ? cat.models : cat.options).filter(matchItem);
    const pub = Math.max(0, Math.min(100, Number(bulkPub) || 0));
    const res = Math.max(0, Math.min(100, Number(bulkRes) || 0));
    setForm((f) => {
      const key = kind === "model" ? "model_rules" : "option_rules";
      const cur = { ...(f[key] || {}) };
      for (const i of items) cur[i.id] = { pub, res };
      return { ...f, [key]: cur };
    });
    toast.success(`Remise appliquée à ${items.length} ${kind === "model" ? "modèle(s)" : "option(s)"}`);
  };
  const clearAll = (kind) => setForm((f) => ({ ...f, [kind === "model" ? "model_rules" : "option_rules"]: {} }));

  const setRuleFromPrice = (kind, item, field, afterVal) => {
    const before = field === "pub" ? priceTtcOf(item) : (item.reseller_ht || 0);
    const after = Math.max(0, Number(afterVal) || 0);
    const pct = before > 0 ? Math.max(0, Math.min(100, (1 - after / before) * 100)) : 0;
    setRule(kind, item.id, field, Math.round(pct * 100) / 100);
  };
  const matchQ = (name) => !q || (name || "").toLowerCase().includes(q.toLowerCase());
  const catOfItem = (i) => (i.category || "").trim() || "Divers";
  const matchItem = (i) => matchQ(i.name) && (promoCat === "all" || catOfItem(i) === promoCat);
  const ruleCats = Array.from(new Set((ruleTab === "model" ? cat.models : cat.options).map(catOfItem))).sort((a, b) => a.localeCompare(b, "fr"));
  const autoRule = (item) => {
    const inc = item.included_options || [];
    if (!inc.length || (form.no_option_promo_models || []).includes(item.id)) return null;
    let pub = 0, res = 0;
    for (const o of cat.options.filter((x) => inc.includes(x.id))) {
      const r = form.option_rules[o.id] || {};
      pub += (o.public_ht || 0) * (Number(r.pub) || 0) / 100;
      res += (o.reseller_ht || 0) * (Number(r.res) || 0) / 100;
    }
    const pct = (v, base) => (base > 0 ? Math.round((v / base) * 10000) / 100 : 0);
    return { pub: pct(pub, item.public_ht), res: pct(res, item.reseller_ht) };
  };
  const marginOf = (item, r) => priceTtcOf(item) * (1 - (Number(r.pub) || 0) / 100) / 1.2 - (item.reseller_ht || 0) * (1 - (Number(r.res) || 0) / 100);
  const lowMargins = () => {
    const out = [];
    const check = (kind, item, r) => {
      if (!r || !(Number(r.pub) || Number(r.res))) return;
      const m = marginOf(item, r);
      if (m <= 0) out.push({ id: `${kind}-${item.id}`, kind, name: item.name, margin: m });
    };
    cat.models.forEach((m) => check("model", m, autoRule(m) || form.model_rules[m.id]));
    cat.options.forEach((o) => check("option", o, form.option_rules[o.id]));
    return out;
  };
  const renderRule = (kind, item) => {
    const key = kind === "model" ? "model_rules" : "option_rules";
    const auto = kind === "model" ? autoRule(item) : null;
    const r = auto || form[key][item.id] || {};
    const pub = r.pub || 0, res = r.res || 0;
    const pubBefore = priceTtcOf(item), pubAfter = pubBefore * (1 - pub / 100);
    const resBefore = item.reseller_ht || 0, resAfter = resBefore * (1 - res / 100);
    const margin = marginOf(item, r);
    return (
      <div key={item.id} className="flex items-center gap-2 py-2 border-b border-border/60" data-testid={`promo-rule-${item.id}`}>
        <span className="flex-1 text-xs leading-snug min-w-0">{item.name}{auto && <Badge variant="secondary" className="ml-1 text-[9px]" title="Remise calculée depuis les options incluses" data-testid={`promo-auto-${item.id}`}>auto · options incluses</Badge>}</span>
        <fieldset disabled={!!auto} className="contents">
        <div className="flex items-center gap-1">
          <span className="w-16 text-right text-[10px] text-muted-foreground tabular-nums">{euro(pubBefore)}</span>
          <Input type="number" min={0} max={100} value={r.pub ?? ""} placeholder="0" onChange={(e) => setRule(kind, item.id, "pub", e.target.value)} data-testid={`promo-pub-${item.id}`} className="w-12 h-7 text-xs font-mono px-1 text-right" />
          <span className="text-[10px] text-muted-foreground">%</span>
          <Input type="number" min={0} value={Math.round(pubAfter)} onChange={(e) => setRuleFromPrice(kind, item, "pub", e.target.value)} data-testid={`promo-pub-after-${item.id}`} className="w-20 h-7 text-xs font-mono px-1 text-right" />
        </div>
        <div className="flex items-center gap-1">
          <span className="w-16 text-right text-[10px] text-muted-foreground tabular-nums">{euro(resBefore)}</span>
          <Input type="number" min={0} max={100} value={r.res ?? ""} placeholder="0" onChange={(e) => setRule(kind, item.id, "res", e.target.value)} data-testid={`promo-res-${item.id}`} className="w-12 h-7 text-xs font-mono px-1 text-right" />
          <span className="text-[10px] text-muted-foreground">%</span>
          <Input type="number" min={0} value={Math.round(resAfter)} onChange={(e) => setRuleFromPrice(kind, item, "res", e.target.value)} data-testid={`promo-res-after-${item.id}`} className="w-20 h-7 text-xs font-mono px-1 text-right" />
        </div>
        </fieldset>
        <span className={`w-20 text-right text-xs font-mono tabular-nums ${margin <= 0 ? "text-destructive font-semibold" : "text-foreground"}`} data-testid={`promo-margin-${item.id}`}>{euro(Math.round(margin))}</span>
        {kind === "model" && (
          <label className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer whitespace-nowrap ml-1" title="Série limitée : les options de ce modèle ne reçoivent pas la remise promo">
            <Checkbox checked={(form.no_option_promo_models || []).includes(item.id)} onCheckedChange={() => toggleNoOpt(item.id)} data-testid={`promo-nooptpromo-${item.id}`} />
            Options hors promo
          </label>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="gap-1" onClick={openNew} data-testid="add-promo"><Plus className="w-4 h-4" /> Nouvelle promotion</Button>
      </div>
      {loading ? (<div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nom</TableHead><TableHead>Gamme</TableHead><TableHead>Pays</TableHead><TableHead>Période</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((p) => (
                <TableRow key={p.id} data-testid={`promo-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-xs">{p.range_key}</TableCell>
                  <TableCell className="text-xs">{(p.country_codes || []).join(", ") || "—"}</TableCell>
                  <TableCell className="text-xs">{(p.start_date ? p.start_date.split("-").reverse().join("/") : "…") + " → " + (p.end_date ? p.end_date.split("-").reverse().join("/") : "…")}</TableCell>
                  <TableCell>{p.active ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-promo-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" title="Dupliquer" onClick={() => openDuplicate(p)} data-testid={`dup-promo-${p.id}`}><Copy className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(p.id)} data-testid={`del-promo-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {list.length === 0 && (<TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">Aucune promotion. Créez-en une pour proposer des remises salon.</TableCell></TableRow>)}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Modifier" : "Nouvelle"} promotion</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="promo-name" className="mt-1.5" /></div>
              <div><Label>Gamme</Label>
                <Select value={form.range_key} onValueChange={(v) => setForm({ ...form, range_key: v, reseller_ids: resellersIn(form.country_codes, v) })}>
                  <SelectTrigger className="mt-1.5" data-testid="promo-range"><SelectValue /></SelectTrigger>
                  <SelectContent>{ranges.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-7"><Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="promo-active" /><Label>Active</Label></div>
              <div><Label>Début</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="promo-start" className="mt-1.5" /></div>
              <div><Label>Fin</Label><Input type="date" value={form.end_date} min={form.start_date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="promo-end" className="mt-1.5" /></div>
              <div><Label>Remise fixe par modèle — public (€ TTC)</Label><Input type="number" min={0} value={form.model_amount_pub_ttc || ""} placeholder="0" onChange={(e) => setForm({ ...form, model_amount_pub_ttc: Number(e.target.value) || 0 })} data-testid="promo-amount-pub" className="mt-1.5" /></div>
              <div><Label>Remise fixe par modèle — revendeur (€ HT)</Label><Input type="number" min={0} value={form.model_amount_res_ht || ""} placeholder="0" onChange={(e) => setForm({ ...form, model_amount_res_ht: Number(e.target.value) || 0 })} data-testid="promo-amount-res" className="mt-1.5" /></div>
              <div className="flex items-center gap-2"><Checkbox checked={!!form.default_off} onCheckedChange={(v) => setForm({ ...form, default_off: !!v })} data-testid="promo-default-off" /><Label>Décochée par défaut (sous éligibilité)</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={!!form.hide_in_prices} onCheckedChange={(v) => setForm({ ...form, hide_in_prices: !!v })} data-testid="promo-hide-in-prices" /><Label>Non proposée dans la page Tarifs</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={!!form.exclusive} onCheckedChange={(v) => setForm({ ...form, exclusive: !!v })} data-testid="promo-exclusive" /><Label>Non cumulable avec d'autres promotions</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={!!form.is_aid} onCheckedChange={(v) => setForm({ ...form, is_aid: !!v })} data-testid="promo-is-aid" /><Label>Aide (remplace « remise » par « aide »)</Label></div>
            </div>
            <div>
              <Label>Pays ciblés <span className="text-xs text-muted-foreground font-normal">(aucun pays coché → la promo ne s'applique nulle part)</span>
                {bulkBtns(() => applyCountries(countries.map((c) => c.code)), () => applyCountries([]), "promo-countries")}</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {countries.map((c) => (
                  <label key={c.code} className="flex items-center gap-1.5 text-xs border border-border rounded-md px-2 py-1 cursor-pointer" data-testid={`promo-country-${c.code}`}>
                    <Checkbox checked={form.country_codes.includes(c.code)} onCheckedChange={() => toggleCountry(c.code)} />{c.code}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer mb-2" data-testid="promo-all-resellers">
                <Checkbox checked={!!form.all_resellers} onCheckedChange={(v) => setForm((f) => ({ ...f, all_resellers: !!v }))} data-testid="promo-all-resellers-check" />
                Tous les revendeurs du pays <span className="text-xs text-muted-foreground font-normal">(y compris les revendeurs ajoutés plus tard)</span>
              </label>
              {!form.all_resellers && <>
              <Label>Revendeurs ciblés <span className="text-xs text-muted-foreground font-normal">(aucun revendeur coché → tous les revendeurs)</span>
                {bulkBtns(() => setForm((f) => ({ ...f, reseller_ids: rangeResellers.map((r) => r.id) })), () => setForm((f) => ({ ...f, reseller_ids: [] })), "promo-resellers")}</Label>
              <div className="flex flex-wrap gap-2 mt-1.5 max-h-32 overflow-y-auto">
                {rangeResellers.length === 0 && <span className="text-xs text-muted-foreground">Aucun revendeur pour cette gamme.</span>}
                {rangeResellers.map((r) => (
                  <label key={r.id} className="flex items-center gap-1.5 text-xs border border-border rounded-md px-2 py-1 cursor-pointer" data-testid={`promo-reseller-${r.id}`} title={[r.company, r.email].filter(Boolean).join(" · ")}>
                    <Checkbox checked={(form.reseller_ids || []).includes(r.id)} onCheckedChange={() => toggleReseller(r.id)} />{r.name || r.company || r.email}
                  </label>
                ))}
              </div>
              </>}
              {!form.default_off && (
                <div className="mt-3">
                  <Label>Décochée par défaut pour <span className="text-xs text-muted-foreground font-normal">(cochez les personnes pour qui cette promo ne doit pas être pré-cochée)</span></Label>
                  <div className="flex flex-wrap gap-2 mt-1.5 max-h-32 overflow-y-auto">
                    <label className="flex items-center gap-1.5 text-xs border border-border rounded-md px-2 py-1 cursor-pointer" data-testid="promo-precheck-off-admin">
                      <Checkbox checked={(form.precheck_off_ids || []).includes("admin")} onCheckedChange={() => togglePrecheckOff("admin")} />Admin
                    </label>
                    {rangeResellers.filter((r) => form.all_resellers || !(form.reseller_ids || []).length || form.reseller_ids.includes(r.id)).map((r) => (
                      <label key={r.id} className="flex items-center gap-1.5 text-xs border border-border rounded-md px-2 py-1 cursor-pointer" data-testid={`promo-precheck-off-${r.id}`}>
                        <Checkbox checked={(form.precheck_off_ids || []).includes(r.id)} onCheckedChange={() => togglePrecheckOff(r.id)} />{r.name || r.company || r.email}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label>Remises par modèle / option <span className="text-xs text-muted-foreground font-normal">(% public affiché au client, % revendeur sur le prix d'achat)</span></Label>
              <Input placeholder="Rechercher un modèle / une option…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="promo-search" className="my-2" />
              <div className="inline-flex rounded-md border border-border p-0.5 mb-2" data-testid="rule-tab">
                {["model", "option"].map((t) => (
                  <button key={t} type="button" onClick={() => { setRuleTab(t); setPromoCat("all"); }} data-testid={`rule-tab-${t}`}
                    className={`px-3 py-1 text-xs font-semibold rounded ${ruleTab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                    {t === "model" ? "Modèles" : "Options"}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="promo-cat-filters">
                {["all", ...ruleCats].map((c) => (
                  <button key={c} type="button" onClick={() => setPromoCat(c)} data-testid={`promo-cat-${c}`}
                    className={`px-2.5 py-0.5 text-[11px] rounded-full border transition-colors ${promoCat === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                    {c === "all" ? "Toutes" : c}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap mb-3 rounded-lg border border-border bg-muted/40 px-3 py-2">
                <span className="text-xs font-semibold text-foreground">Remise</span>
                <span className="text-xs text-muted-foreground">% prix public :</span>
                <Input type="number" min={0} max={100} value={bulkPub} onChange={(e) => { const v = e.target.value; if (ruleTab === "option" && (!Number(bulkRes) || Number(bulkRes) === Number(bulkPub))) setBulkRes(v); setBulkPub(v); }} data-testid="promo-bulk-pub" className="w-20 h-7 text-xs font-mono" />
                <span className="text-xs text-muted-foreground">% prix revendeur :</span>
                <Input type="number" min={0} max={100} value={bulkRes} onChange={(e) => setBulkRes(e.target.value)} data-testid="promo-bulk-res" className="w-20 h-7 text-xs font-mono" />
                <Button type="button" size="sm" variant="outline" onClick={() => applyAll(ruleTab)} data-testid="promo-check-all">Tout cocher</Button>
                <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={() => clearAll(ruleTab)} data-testid="promo-uncheck-all">Tout décocher</Button>
                {ruleTab === "model" && (
                  <span className="flex items-center ml-auto text-xs text-muted-foreground">Options hors promo :{bulkBtns(() => setAllNoOpt(true), () => setAllNoOpt(false), "promo-noopt")}</span>
                )}
              </div>
              <div className="hidden sm:flex items-center gap-2 px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span className="flex-1">{ruleTab === "model" ? "Modèle" : "Option"}</span>
                <span className="w-16 text-right">Public TTC avant</span><span className="w-12 text-right">%</span><span className="w-20 text-right">Public TTC après</span>
                <span className="w-16 text-right">Rev. HT avant</span><span className="w-12 text-right">%</span><span className="w-20 text-right">Rev. HT après</span><span className="w-20 text-right">Marge rev. HT</span>
              </div>
              <div className="max-h-72 overflow-y-auto pr-1 border border-border rounded-md p-2" data-testid="rule-list">
                {(ruleTab === "model" ? cat.models : cat.options).filter(matchItem).map((i) => renderRule(ruleTab, i))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={() => save()} data-testid="promo-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lowList} onOpenChange={(v) => !v && setLowList(null)}>
        <DialogContent className="max-w-lg" data-testid="low-margin-dialog">
          <DialogHeader><DialogTitle>Marge revendeur négative ou nulle</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{(lowList || []).length} ligne(s) remisée(s) donnent une marge revendeur ≤ 0 € :</p>
          <div className="max-h-64 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="low-margin-list">
            {(lowList || []).map((l) => (
              <div key={l.id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`low-margin-${l.id}`}>
                <Badge variant="secondary" className="text-[9px]">{l.kind === "model" ? "Modèle" : "Option"}</Badge>
                <span className="flex-1 min-w-0">{l.name}</span>
                <span className="font-mono font-semibold text-destructive">{euro(Math.round(l.margin))}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLowList(null)} data-testid="low-margin-fix">Corriger</Button>
            <Button variant="destructive" onClick={() => save(true)} data-testid="low-margin-force">Enregistrer quand même</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminDashboard() {
  const [ranges, setRanges] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/ranges").then(({ data }) => setRanges(data));
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mb-1">Administration</h1>
          <p className="text-muted-foreground text-sm">Gérez vos modèles, options, prix et revendeurs.</p>
        </div>
        <OptionsTrash kind="expos" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={FileBarChart} label="Devis" value={stats?.quotes ?? "—"} />
        <StatCard icon={Users} label="Revendeurs" value={stats?.resellers ?? "—"} />
        <StatCard icon={Package} label="Modèles" value={stats?.models ?? "—"} />
        <StatCard icon={ListChecks} label="Options" value={stats?.options ?? "—"} />
      </div>

      {ranges.length > 0 && (
        <Tabs defaultValue="models">
          <TabsList className="w-full sm:w-auto justify-start h-auto flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="models" data-testid="tab-models">Modèles</TabsTrigger>
            <TabsTrigger value="options" data-testid="tab-options">Options</TabsTrigger>
            <TabsTrigger value="resellers" data-testid="tab-resellers">Revendeurs</TabsTrigger>
            <TabsTrigger value="stock-orders" data-testid="tab-stock-orders">
              <Package className="w-4 h-4 mr-1.5" /> Commandes stock
            </TabsTrigger>
            <TabsTrigger value="tr-history" data-testid="tab-tr-history">Corrections traductions</TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import">
              <Upload className="w-4 h-4 mr-1.5" /> Import Excel
            </TabsTrigger>
            <TabsTrigger value="countries" data-testid="tab-countries">
              <Globe className="w-4 h-4 mr-1.5" /> Pays & TVA
            </TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Prix par pays</TabsTrigger>
            <TabsTrigger value="company" data-testid="tab-company">
              <Building2 className="w-4 h-4 mr-1.5" /> Entreprise
            </TabsTrigger>
            <TabsTrigger value="promotions" data-testid="tab-promotions">
              <Percent className="w-4 h-4 mr-1.5" /> Promotions
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos">
              <ImageIcon className="w-4 h-4 mr-1.5" /> Photos
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">
              <CalendarDays className="w-4 h-4 mr-1.5" /> Événements & lieux
            </TabsTrigger>
          </TabsList>
          <TabsContent value="models" className="mt-6"><CatalogTab kind="model" ranges={ranges} /></TabsContent>
          <TabsContent value="options" className="mt-6"><CatalogTab kind="option" ranges={ranges} /></TabsContent>
          <TabsContent value="resellers" className="mt-6"><ResellersTab /></TabsContent>
          <TabsContent value="stock-orders" className="mt-6"><StockOrdersTab /></TabsContent>
          <TabsContent value="tr-history" className="mt-6"><TranslationHistoryTab /></TabsContent>
          <TabsContent value="import" className="mt-6"><ImportTab ranges={ranges} /></TabsContent>
          <TabsContent value="countries" className="mt-6"><CountriesTab /></TabsContent>
          <TabsContent value="pricing" className="mt-6"><PricingParamsTab /></TabsContent>
          <TabsContent value="company" className="mt-6"><CompanyTab /></TabsContent>
          <TabsContent value="promotions" className="mt-6"><PromotionsTab ranges={ranges} /></TabsContent>
          <TabsContent value="photos" className="mt-6"><SchematicsTab ranges={ranges} /></TabsContent>
          <TabsContent value="events" className="mt-6"><EventsTab /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
